// src/screens/AdminBrandedEventsScreen.js
// ============================================================================
// ADMIN BRANDED EVENTS & SPONSORS (EVERGREEN_CREATOR_PLAN.md 6D, step 3.8)
//
// Admin-only control panel for event branding, organizer attribution,
// publisher permission on file, and multi-sponsor approvals/logos.
// No money or amount fields exist on this screen.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Switch,
  ActivityIndicator,
  Image,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { useAdminAuth } from '../hooks/useAdminAuth';
import { db, functions } from '../../firebaseConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import {
  roundTimesFromGap,
  normalizeRoundStartTimes,
  validateRoundScheduleClient,
  buildRoundSchedulePayload,
  formatRoundLocalTime,
  formatGapLabel,
  mapRoundScheduleError,
  toEpochMs,
} from '../utils/roundSchedule';
import { RoundScheduleEditor } from '../components/RoundScheduleEditor';

let DatePicker = null;
try {
  DatePicker = require('../utils/DatePicker.web.js').default;
} catch (_) {
  DatePicker = null;
}

const SPONSOR_LEVELS = [
  { key: 'presenting', label: 'Presenting' },
  { key: 'featured', label: 'Featured' },
  { key: 'community', label: 'Community' },
  { key: 'prize', label: 'Giveaway' },
];

const EMPTY_SPONSOR_FORM = {
  sponsorshipId: '',
  inquiryId: '',
  sponsorPath: '',
  companyName: '',
  publicName: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  level: 'featured',
  categoryCheckPassed: false,
  categoryNote: '',
  url: '',
  prizeNote: '',
  sortOrder: '0',
};

const EMPTY_STAGE = { name: '', when: '', where: '' };

function buildChampionshipForm(champ) {
  if (!champ || typeof champ !== 'object') {
    return {
      slug: '',
      venueName: '',
      venueCity: '',
      stages: [{ ...EMPTY_STAGE }],
      rulesText: '',
    };
  }
  const stages = Array.isArray(champ.stages) && champ.stages.length > 0
    ? champ.stages.slice(0, 4).map((st) => ({
        name: st?.name || '',
        when: st?.when || '',
        where: st?.where || '',
      }))
    : [{ ...EMPTY_STAGE }];
  return {
    slug: champ.slug || '',
    venueName: champ.venue?.name || '',
    venueCity: champ.venue?.city || '',
    stages,
    rulesText: champ.rulesText || '',
  };
}

function formatInquiryDate(val) {
  if (!val) return '';
  if (typeof val === 'number') return new Date(val).toISOString().slice(0, 10);
  if (typeof val?.toMillis === 'function') {
    return new Date(val.toMillis()).toISOString().slice(0, 10);
  }
  const sec = Number(val?._seconds ?? val?.seconds);
  if (Number.isFinite(sec)) {
    return new Date(sec * 1000).toISOString().slice(0, 10);
  }
  return '';
}

export default function AdminBrandedEventsScreen({
  navigation,
  initialAdminState = null,
  initialEventData = null,
}) {
  const { user } = useAdminAuth();
  const [adminStatus, setAdminStatus] = useState(
    initialAdminState || 'checking'
  ); // 'checking' | 'allowed' | 'denied'

  const [eventOptions, setEventOptions] = useState([]);
  const [tournamentIdInput, setTournamentIdInput] = useState(
    initialEventData?.tournamentId || ''
  );
  const [selectedTournamentId, setSelectedTournamentId] = useState(
    initialEventData?.tournamentId || ''
  );
  const [eventTitle, setEventTitle] = useState(
    initialEventData?.title || initialEventData?.name || ''
  );

  // Permission state
  const [permissionOnFile, setPermissionOnFile] = useState(
    initialEventData?.permissionOnFile === true
  );
  const [permissionNote, setPermissionNote] = useState(
    initialEventData?.permissionNote || ''
  );

  // Branding state
  const [branded, setBranded] = useState(initialEventData?.branded === true);
  const [sponsorSignupOpen, setSponsorSignupOpen] = useState(
    initialEventData?.branded === true &&
      initialEventData?.sponsorSignupOpen === true
  );
  const [organizerName, setOrganizerName] = useState(
    initialEventData?.organizer?.name || ''
  );
  const [organizerUrl, setOrganizerUrl] = useState(
    initialEventData?.organizer?.url || ''
  );
  const [publisherDisclaimer, setPublisherDisclaimer] = useState(
    initialEventData?.publisherDisclaimer || ''
  );

  // Championship page state
  const [championshipForm, setChampionshipForm] = useState(
    buildChampionshipForm(initialEventData?.championship)
  );
  const [savedChampionshipSlug, setSavedChampionshipSlug] = useState(
    initialEventData?.championship?.slug || ''
  );

  // Sponsorships list + form
  const [sponsorships, setSponsorships] = useState(
    Array.isArray(initialEventData?.sponsorships)
      ? initialEventData.sponsorships
      : []
  );
  const [sponsorForm, setSponsorForm] = useState(EMPTY_SPONSOR_FORM);

  // Sponsor sign-ups (event inquiries + unassigned general inquiries)
  const [eventInquiries, setEventInquiries] = useState(
    Array.isArray(initialEventData?.eventInquiries)
      ? initialEventData.eventInquiries
      : []
  );
  const [generalInquiries, setGeneralInquiries] = useState(
    Array.isArray(initialEventData?.generalInquiries)
      ? initialEventData.generalInquiries
      : []
  );

  const [busy, setBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Round schedule state for the selected event
  // Default to 120 minutes when durationMinutes is absent on the event doc,
  // matching adminSetRoundSchedule's estimatedEndTime fallback (events.js:3260).
  const DEFAULT_EVENT_DURATION_MINUTES = 120;
  const initTotalRounds = Math.max(1, Math.floor(Number(initialEventData?.totalRounds)) || 1);
  const initStartAtMs = toEpochMs(initialEventData?.startAt);
  const initDurationMin = Number(initialEventData?.durationMinutes) || DEFAULT_EVENT_DURATION_MINUTES;
  const initSchedMode = initialEventData?.roundScheduleMode === 'scheduled' ? 'scheduled' : 'gap';
  const initGapMin = Number(initialEventData?.interRoundGapMinutes) || 5;
  const initNormalizedTimes = normalizeRoundStartTimes(initialEventData?.roundStartTimes, initTotalRounds);
  const [eventTotalRounds, setEventTotalRounds] = useState(initTotalRounds);
  const [eventStartAtMs, setEventStartAtMs] = useState(initStartAtMs);
  const [eventDurationMinutes, setEventDurationMinutes] = useState(initDurationMin);
  const [savedRoundScheduleMode, setSavedRoundScheduleMode] = useState(initSchedMode);
  const [savedInterRoundGapMinutes, setSavedInterRoundGapMinutes] = useState(initGapMin);
  const [savedRoundStartTimes, setSavedRoundStartTimes] = useState(initNormalizedTimes);
  const [roundScheduleMode, setRoundScheduleMode] = useState(initSchedMode);
  const [interRoundGapMinutes, setInterRoundGapMinutes] = useState(initGapMin);
  const [roundStartTimes, setRoundStartTimes] = useState(initNormalizedTimes);
  const [roundScheduleStatus, setRoundScheduleStatus] = useState('');
  const [roundScheduleError, setRoundScheduleError] = useState('');
  const [roundScheduleLoadError, setRoundScheduleLoadError] = useState('');

  const defaultAdminRoundTimes = roundTimesFromGap({
    totalRounds: eventTotalRounds,
    startAtMs: eventStartAtMs,
    gapMinutes: interRoundGapMinutes,
    perRoundMinutes: eventDurationMinutes,
  });

  const effectiveAdminRoundStartTimes = (() => {
    const out = {};
    for (let r = 2; r <= eventTotalRounds; r++) {
      out[r] = Object.prototype.hasOwnProperty.call(roundStartTimes, r)
        ? roundStartTimes[r]
        : defaultAdminRoundTimes[r];
    }
    return out;
  })();

  const savedDefaultRoundTimes = roundTimesFromGap({
    totalRounds: eventTotalRounds,
    startAtMs: eventStartAtMs,
    gapMinutes: savedInterRoundGapMinutes,
    perRoundMinutes: eventDurationMinutes,
  });

  useEffect(() => {
    if (initialAdminState) return;
    let cancelled = false;
    async function verifyAdmin() {
      if (!user?.uid) {
        if (!cancelled) setAdminStatus('denied');
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        const isAdmin = snap.exists() && snap.data()?.role === 'admin';
        if (!cancelled) setAdminStatus(isAdmin ? 'allowed' : 'denied');
      } catch (_) {
        if (!cancelled) setAdminStatus('denied');
      }
    }
    verifyAdmin();
    return () => {
      cancelled = true;
    };
  }, [user, initialAdminState]);

  const loadSelectableEvents = useCallback(async () => {
    try {
      const q = query(
        collection(db, 'tournaments'),
        where('status', 'in', ['scheduled', 'locked']),
        limit(40)
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({
        id: d.id,
        title: d.data()?.title || d.data()?.name || d.id,
        status: d.data()?.status || 'scheduled',
        branded: d.data()?.branded === true,
      }));
      setEventOptions(list);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (adminStatus === 'allowed' && !initialEventData) {
      loadSelectableEvents();
    }
  }, [adminStatus, initialEventData, loadSelectableEvents]);

  const clearRoundScheduleData = (errMsg = '') => {
    setEventTotalRounds(0);
    setEventStartAtMs(null);
    setEventDurationMinutes(DEFAULT_EVENT_DURATION_MINUTES);
    setSavedRoundScheduleMode('gap');
    setSavedInterRoundGapMinutes(5);
    setSavedRoundStartTimes({});
    setRoundScheduleMode('gap');
    setInterRoundGapMinutes(5);
    setRoundStartTimes({});
    setRoundScheduleStatus('');
    setRoundScheduleError(errMsg);
    setRoundScheduleLoadError(errMsg);
  };

  const applyRoundScheduleData = (docData) => {
    if (!docData) return;
    const tr = Math.max(1, Math.floor(Number(docData.totalRounds)) || 1);
    const sMs = toEpochMs(docData.startAt);
    // Default to 120 minutes when durationMinutes is absent on the event doc,
    // matching adminSetRoundSchedule's estimatedEndTime fallback (events.js:3260).
    const durMin = Number(docData.durationMinutes) || DEFAULT_EVENT_DURATION_MINUTES;
    const mode = docData.roundScheduleMode === 'scheduled' ? 'scheduled' : 'gap';
    const gap = Number(docData.interRoundGapMinutes) || 5;
    const normTimes = normalizeRoundStartTimes(docData.roundStartTimes, tr);
    setEventTotalRounds(tr);
    setEventStartAtMs(sMs);
    setEventDurationMinutes(durMin);
    setSavedRoundScheduleMode(mode);
    setSavedInterRoundGapMinutes(gap);
    setSavedRoundStartTimes(normTimes);
    setRoundScheduleMode(mode);
    setInterRoundGapMinutes(gap);
    setRoundStartTimes(normTimes);
    setRoundScheduleStatus('');
    setRoundScheduleError('');
    setRoundScheduleLoadError('');
  };

  const applyBrandingPayload = (data) => {
    if (!data) return;
    setSelectedTournamentId(data.tournamentId || '');
    setTournamentIdInput(data.tournamentId || '');
    setEventTitle(data.title || data.name || '');
    setPermissionOnFile(data.permissionOnFile === true);
    setPermissionNote(data.permissionNote || '');
    const isBranded = data.branded === true;
    setBranded(isBranded);
    setSponsorSignupOpen(isBranded && data.sponsorSignupOpen === true);
    setOrganizerName(data.organizer?.name || '');
    setOrganizerUrl(data.organizer?.url || '');
    setPublisherDisclaimer(data.publisherDisclaimer || '');
    setChampionshipForm(buildChampionshipForm(data.championship));
    setSavedChampionshipSlug(data.championship?.slug || '');
    setSponsorships(Array.isArray(data.sponsorships) ? data.sponsorships : []);
    if (
      data.totalRounds !== undefined ||
      data.startAt !== undefined ||
      data.roundScheduleMode !== undefined
    ) {
      applyRoundScheduleData(data);
    }
  };

  const handleLoadEvent = async (targetId) => {
    const id = String(targetId ?? tournamentIdInput ?? '').trim();
    if (!id) {
      setErrorMessage('Enter or select a tournament ID first.');
      return;
    }
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const call = httpsCallable(functions, 'adminGetEventBranding');
      const res = await call({ tournamentId: id });
      applyBrandingPayload(res?.data);
      try {
        const evSnap = await getDoc(doc(db, 'tournaments', id));
        if (evSnap && evSnap.exists()) {
          applyRoundScheduleData(evSnap.data() || {});
        } else {
          clearRoundScheduleData("Couldn't load this event's rounds. Try again.");
        }
      } catch (_) {
        clearRoundScheduleData("Couldn't load this event's rounds. Try again.");
      }
      try {
        const listInqCall = httpsCallable(
          functions,
          'adminListEventSponsorInquiries'
        );
        const inqRes = await listInqCall({ tournamentId: id });
        if (Array.isArray(inqRes?.data?.inquiries)) {
          setEventInquiries(inqRes.data.inquiries);
        }
      } catch (_) {}
      try {
        const pendingCall = httpsCallable(functions, 'adminGetPendingAdReviews');
        const pendRes = await pendingCall({});
        if (Array.isArray(pendRes?.data?.inquiries)) {
          setGeneralInquiries(
            pendRes.data.inquiries.filter((inq) => !inq.tournamentId)
          );
        }
      } catch (_) {}
      setStatusMessage(`Loaded branding for event ${id}.`);
    } catch (err) {
      setErrorMessage(err?.message || 'Could not load event branding.');
    } finally {
      setBusy(false);
    }
  };

  const handleAdminRoundScheduleModeChange = (nextMode) => {
    setRoundScheduleMode(nextMode);
    setRoundScheduleError('');
    setRoundScheduleStatus('');
    if (nextMode === 'scheduled') {
      setRoundStartTimes((prev) => {
        const next = {};
        for (let r = 2; r <= eventTotalRounds; r++) {
          next[r] = Object.prototype.hasOwnProperty.call(prev, r) && Number.isFinite(prev[r])
            ? prev[r]
            : defaultAdminRoundTimes[r];
        }
        return next;
      });
    }
  };

  const handleAdminGapMinutesChange = (min) => {
    setInterRoundGapMinutes(min);
    setRoundStartTimes({});
    setRoundScheduleError('');
    setRoundScheduleStatus('');
  };

  const handleAdminRoundTimeChange = (roundNum, val) => {
    const nextMs = toEpochMs(val);
    setRoundScheduleError('');
    setRoundScheduleStatus('');
    setRoundStartTimes((prev) => ({
      ...defaultAdminRoundTimes,
      ...prev,
      [roundNum]: nextMs,
    }));
  };

  const handleSaveRoundSchedule = async () => {
    if (!selectedTournamentId) {
      setErrorMessage('Load an event first.');
      return;
    }
    if (roundScheduleMode === 'scheduled') {
      const checkRes = validateRoundScheduleClient({
        mode: 'scheduled',
        totalRounds: eventTotalRounds,
        startAtMs: eventStartAtMs,
        roundStartTimes: effectiveAdminRoundStartTimes,
      });
      if (!checkRes.ok) {
        setRoundScheduleError(checkRes.error);
        setErrorMessage(checkRes.error);
        return;
      }
    }
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    setRoundScheduleError('');
    setRoundScheduleStatus('');
    try {
      const call = httpsCallable(functions, 'adminSetRoundSchedule');
      const payload = {
        eventId: selectedTournamentId,
        ...buildRoundSchedulePayload({
          mode: roundScheduleMode,
          interRoundGapMinutes,
          roundStartTimes: effectiveAdminRoundStartTimes,
          totalRounds: eventTotalRounds,
        }),
      };
      const res = await call(payload);
      const data = res?.data || {};
      const nextMode = data.roundScheduleMode || roundScheduleMode;
      const nextGap = Number(data.interRoundGapMinutes) || interRoundGapMinutes;
      const nextTimes = data.roundStartTimes
        ? normalizeRoundStartTimes(data.roundStartTimes, eventTotalRounds)
        : effectiveAdminRoundStartTimes;
      setSavedRoundScheduleMode(nextMode);
      setSavedInterRoundGapMinutes(nextGap);
      setSavedRoundStartTimes(nextTimes);
      const answer = nextMode === 'scheduled'
        ? 'Saved round times. Each round now has its own start time.'
        : `Saved round times. Rounds use a ${formatGapLabel(nextGap)} gap.`;
      setRoundScheduleStatus(answer);
      setStatusMessage(answer);
    } catch (err) {
      const plain = mapRoundScheduleError(err) || 'Could not save round times.';
      setRoundScheduleError(plain);
      setErrorMessage(plain);
    } finally {
      setBusy(false);
    }
  };

  const handleSavePermission = async () => {
    if (!selectedTournamentId) {
      setErrorMessage('Load an event first.');
      return;
    }
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const call = httpsCallable(functions, 'adminSetEventSponsorPermission');
      await call({
        tournamentId: selectedTournamentId,
        permissionOnFile,
        permissionNote,
      });
      if (!permissionOnFile) {
        setBranded(false);
        setSponsorSignupOpen(false);
      }
      setStatusMessage('Saved publisher/organizer permission.');
    } catch (err) {
      setErrorMessage(err?.message || 'Could not save permission.');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveBranding = async () => {
    if (!selectedTournamentId) {
      setErrorMessage('Load an event first.');
      return;
    }
    if (branded && !permissionOnFile) {
      setErrorMessage('Permission on file is required before branding an event.');
      return;
    }
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const call = httpsCallable(functions, 'adminSetEventBranding');
      const organizer = organizerName.trim()
        ? {
            name: organizerName.trim(),
            url: organizerUrl.trim() || null,
          }
        : null;
      await call({
        tournamentId: selectedTournamentId,
        branded,
        organizer,
        publisherDisclaimer: publisherDisclaimer.trim() || null,
      });
      if (!branded) {
        setSponsorSignupOpen(false);
      }
      setStatusMessage('Saved event branding.');
    } catch (err) {
      setErrorMessage(err?.message || 'Could not save event branding.');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleSponsorSignupOpen = async (nextOpen) => {
    if (!selectedTournamentId) {
      setErrorMessage('Load an event first.');
      return;
    }
    if (!branded && nextOpen) {
      setErrorMessage(
        'Turn on and save Branded event before opening sponsor sign-ups.'
      );
      return;
    }
    setSponsorSignupOpen(Boolean(nextOpen));
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const call = httpsCallable(functions, 'adminSetSponsorSignupOpen');
      await call({
        tournamentId: selectedTournamentId,
        open: Boolean(nextOpen),
      });
      setStatusMessage('Updated sponsor sign-up availability.');
    } catch (err) {
      setErrorMessage(err?.message || 'Could not update sponsor sign-up availability.');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveChampionshipPage = async () => {
    if (!selectedTournamentId) {
      setErrorMessage('Load an event first.');
      return;
    }
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const call = httpsCallable(functions, 'adminSetChampionshipPage');
      const cleanSlug = championshipForm.slug.trim().toLowerCase();
      const payload = {
        slug: cleanSlug,
        venue: {
          name: championshipForm.venueName.trim(),
          city: championshipForm.venueCity.trim(),
        },
        stages: championshipForm.stages.slice(0, 4).map((st) => ({
          name: String(st.name || '').trim(),
          when: String(st.when || '').trim(),
          where: String(st.where || '').trim(),
        })),
        rulesText: championshipForm.rulesText.trim(),
      };
      await call({
        tournamentId: selectedTournamentId,
        championship: payload,
      });
      setSavedChampionshipSlug(cleanSlug);
      setStatusMessage('Saved championship page.');
    } catch (err) {
      setErrorMessage(err?.message || 'Could not save championship page.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveChampionshipPage = async () => {
    if (!selectedTournamentId) {
      setErrorMessage('Load an event first.');
      return;
    }
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const call = httpsCallable(functions, 'adminSetChampionshipPage');
      await call({
        tournamentId: selectedTournamentId,
        championship: null,
      });
      setChampionshipForm(buildChampionshipForm(null));
      setSavedChampionshipSlug('');
      setStatusMessage('Removed championship page.');
    } catch (err) {
      setErrorMessage(err?.message || 'Could not remove championship page.');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveSponsorship = async () => {
    if (!selectedTournamentId) {
      setErrorMessage('Load an event first.');
      return;
    }
    if (!sponsorForm.companyName.trim() && !sponsorForm.publicName.trim()) {
      setErrorMessage('Company name and public display name are required.');
      return;
    }
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const call = httpsCallable(functions, 'adminUpsertSponsorship');
      await call({
        ...(sponsorForm.sponsorshipId ? { sponsorshipId: sponsorForm.sponsorshipId } : {}),
        ...(!sponsorForm.sponsorshipId && sponsorForm.inquiryId
          ? { inquiryId: sponsorForm.inquiryId }
          : {}),
        ...(sponsorForm.sponsorPath ? { sponsorPath: sponsorForm.sponsorPath } : {}),
        tournamentId: selectedTournamentId,
        companyName: sponsorForm.companyName.trim() || sponsorForm.publicName.trim(),
        publicName: sponsorForm.publicName.trim() || sponsorForm.companyName.trim(),
        contactName: sponsorForm.contactName.trim(),
        contactEmail: sponsorForm.contactEmail.trim(),
        contactPhone: sponsorForm.contactPhone.trim(),
        level: sponsorForm.level,
        categoryCheckPassed: sponsorForm.categoryCheckPassed === true,
        categoryNote: sponsorForm.categoryNote.trim(),
        url: sponsorForm.url.trim() || null,
        prizeNote: sponsorForm.prizeNote.trim() || null,
        sortOrder: Number.isFinite(Number(sponsorForm.sortOrder))
          ? Math.trunc(Number(sponsorForm.sortOrder))
          : 0,
      });
      setSponsorForm(EMPTY_SPONSOR_FORM);
      await handleLoadEvent(selectedTournamentId);
      setStatusMessage('Saved sponsorship.');
    } catch (err) {
      setErrorMessage(err?.message || 'Could not save sponsorship.');
    } finally {
      setBusy(false);
    }
  };

  const handleCreateSponsorFromInquiry = (inq) => {
    if (!inq) return;
    const validLevels = ['presenting', 'featured', 'community', 'prize'];
    const level = validLevels.includes(inq.levelInterest)
      ? inq.levelInterest
      : 'featured';
    setSponsorForm({
      sponsorshipId: '',
      inquiryId: inq.inquiryId || inq.id || '',
      sponsorPath: inq.sponsorPath || '',
      companyName: inq.companyName || '',
      publicName: inq.companyName || '',
      contactName: inq.contactName || '',
      contactEmail: inq.email || '',
      contactPhone: inq.phone || '',
      level,
      categoryCheckPassed: false,
      categoryNote: '',
      url: '',
      prizeNote: '',
      sortOrder: '0',
    });
  };

  const handleDeclineInquiry = async (inquiryId) => {
    if (!inquiryId) return;
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const call = httpsCallable(functions, 'adminDeclineInquiry');
      await call({ inquiryId });
      await handleLoadEvent(selectedTournamentId);
      setStatusMessage('Declined sponsor sign-up.');
    } catch (err) {
      setErrorMessage(err?.message || 'Could not decline sign-up.');
    } finally {
      setBusy(false);
    }
  };

  const handleAssignInquiry = async (inquiryId) => {
    if (!inquiryId || !selectedTournamentId) return;
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const call = httpsCallable(functions, 'adminAssignInquiryToEvent');
      await call({ inquiryId, tournamentId: selectedTournamentId });
      await handleLoadEvent(selectedTournamentId);
      setStatusMessage('Assigned sign-up to this event.');
    } catch (err) {
      setErrorMessage(err?.message || 'Could not assign sign-up.');
    } finally {
      setBusy(false);
    }
  };

  const handleStatusChange = async (sponsorshipId, nextStatus) => {
    setBusy(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const call = httpsCallable(functions, 'adminSetSponsorshipStatus');
      await call({ sponsorshipId, status: nextStatus });
      await handleLoadEvent(selectedTournamentId);
      setStatusMessage(`Updated sponsorship status to ${nextStatus}.`);
    } catch (err) {
      setErrorMessage(err?.message || 'Could not update sponsorship status.');
    } finally {
      setBusy(false);
    }
  };

  const handlePickAndUploadLogo = (sponsorshipId) => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      setErrorMessage('Logo upload is supported in the web admin console.');
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/webp,image/jpeg';
    input.onchange = async (e) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) {
        setErrorMessage('Logo file must be 2 MB or smaller.');
        return;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        setBusy(true);
        setErrorMessage('');
        setStatusMessage('');
        try {
          const call = httpsCallable(functions, 'adminUploadSponsorLogo');
          await call({
            sponsorshipId,
            imageBase64: String(reader.result || ''),
          });
          await handleLoadEvent(selectedTournamentId);
          setStatusMessage('Uploaded sponsor logo.');
        } catch (err) {
          setErrorMessage(err?.message || 'Could not upload sponsor logo.');
        } finally {
          setBusy(false);
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const startEditSponsorship = (sp) => {
    setSponsorForm({
      sponsorshipId: sp.sponsorshipId || '',
      inquiryId: '',
      sponsorPath: sp.sponsorPath || '',
      companyName: sp.companyName || '',
      publicName: sp.publicName || '',
      contactName: sp.contactName || '',
      contactEmail: sp.contactEmail || '',
      contactPhone: sp.contactPhone || '',
      level: sp.level || 'featured',
      categoryCheckPassed: sp.categoryCheckPassed === true,
      categoryNote: sp.categoryNote || '',
      url: sp.url || '',
      prizeNote: sp.prizeNote || '',
      sortOrder: String(sp.sortOrder ?? 0),
    });
  };

  if (adminStatus === 'checking') {
    return (
      <SafeAreaView style={styles.safe}>
        <Header navigation={navigation} />
        <View style={styles.centerBox}>
          <ActivityIndicator color="#FCD000" size="large" />
          <Text style={styles.mutedText}>Checking admin access...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (adminStatus !== 'allowed') {
    return (
      <SafeAreaView style={styles.safe}>
        <Header navigation={navigation} />
        <View style={styles.centerBox}>
          <Ionicons name="lock-closed" size={40} color="#ff5a5a" />
          <Text style={styles.deniedTitle}>Access Denied</Text>
          <Text style={styles.mutedText}>Admin privileges required.</Text>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <Header navigation={navigation} />
      <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
        <View style={styles.headerRow}>
          <View>
            <Text style={styles.pageTitle}>Branded Events & Sponsors</Text>
            <Text style={styles.pageSubtitle}>
              Configure organizer attribution, publisher permission on file, and approved event sponsors.
            </Text>
          </View>
          <TouchableOpacity
            style={styles.backBtn}
            onPress={() => navigation?.navigate?.('AdminDashboard')}
          >
            <Text style={styles.backBtnText}>Back to Admin</Text>
          </TouchableOpacity>
        </View>

        {Boolean(errorMessage) && (
          <View style={styles.errorBanner}>
            <Text style={styles.errorBannerText}>{errorMessage}</Text>
          </View>
        )}
        {Boolean(statusMessage) && (
          <View style={styles.okBanner}>
            <Text style={styles.okBannerText}>{statusMessage}</Text>
          </View>
        )}

        {/* 1. Event Selector */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>1. Select Event</Text>
          <View style={styles.inlineRow}>
            <TextInput
              style={[styles.input, { flex: 1 }]}
              value={tournamentIdInput}
              onChangeText={setTournamentIdInput}
              placeholder="Tournament ID"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Tournament ID"
            />
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() => handleLoadEvent(tournamentIdInput)}
              disabled={busy}
            >
              <Text style={styles.primaryBtnText}>Load Event</Text>
            </TouchableOpacity>
          </View>

          {eventOptions.length > 0 && (
            <View style={styles.pillWrap}>
              {eventOptions.map((ev) => (
                <TouchableOpacity
                  key={ev.id}
                  style={[
                    styles.eventPill,
                    selectedTournamentId === ev.id && styles.eventPillActive,
                  ]}
                  onPress={() => handleLoadEvent(ev.id)}
                >
                  <Text style={styles.eventPillText}>
                    {`${ev.title} (${ev.status})${ev.branded ? ' [Branded]' : ''}`}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

          {Boolean(selectedTournamentId) && (
            <Text style={styles.selectedMeta}>
              {`Selected event: ${eventTitle ? `${eventTitle} (${selectedTournamentId})` : selectedTournamentId}`}
            </Text>
          )}
        </View>

        {/* 2. Permission Section */}
        {Boolean(selectedTournamentId) && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>2. Publisher / Organizer Permission</Text>
            <View style={styles.switchRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Permission on file</Text>
                <Text style={styles.hint}>
                  Required before branding an event or approving any sponsor.
                </Text>
              </View>
              <Switch
                accessibilityLabel="Permission on file"
                value={permissionOnFile}
                onValueChange={setPermissionOnFile}
                disabled={busy}
              />
            </View>
            <Text style={styles.label}>Permission note</Text>
            <TextInput
              style={styles.input}
              value={permissionNote}
              onChangeText={setPermissionNote}
              placeholder="Community license or organizer confirmation note"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Permission note"
              maxLength={500}
            />
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={handleSavePermission}
              disabled={busy}
            >
              <Text style={styles.primaryBtnText}>Save Permission</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* 3. Event Branding Section */}
        {Boolean(selectedTournamentId) && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>3. Event Branding</Text>
            <View style={styles.switchRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Branded event</Text>
                <Text style={styles.hint}>
                  {permissionOnFile
                    ? 'When enabled, approved sponsors and organizer attribution appear publicly.'
                    : 'Turn on and save Permission on file above before enabling Branded event.'}
                </Text>
              </View>
              <Switch
                accessibilityLabel="Branded event"
                value={branded}
                onValueChange={setBranded}
                disabled={!permissionOnFile || busy}
              />
            </View>

            <Text style={styles.label}>Organizer name (optional, up to 60 chars)</Text>
            <TextInput
              style={styles.input}
              value={organizerName}
              onChangeText={setOrganizerName}
              placeholder="Organizer name"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Organizer name"
              maxLength={60}
            />

            <Text style={styles.label}>Organizer URL (optional, https only)</Text>
            <TextInput
              style={styles.input}
              value={organizerUrl}
              onChangeText={setOrganizerUrl}
              placeholder="https://example.com"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Organizer URL (https)"
              maxLength={2048}
            />

            <Text style={styles.label}>Publisher disclaimer (optional, up to 200 chars)</Text>
            <TextInput
              style={styles.input}
              value={publisherDisclaimer}
              onChangeText={setPublisherDisclaimer}
              placeholder="This tournament is not affiliated with or sponsored by the game publisher."
              placeholderTextColor="#6e7688"
              accessibilityLabel="Publisher disclaimer"
              maxLength={200}
            />

            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={handleSaveBranding}
              disabled={busy}
            >
              <Text style={styles.primaryBtnText}>Save Event Branding</Text>
            </TouchableOpacity>

            <View style={styles.divider} />
            <View style={styles.switchRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Taking sponsors</Text>
                <Text style={styles.hint}>
                  {branded
                    ? 'When enabled, this event appears in the public sponsor sign-up picker.'
                    : 'Turn on and save Branded event before opening sponsor sign-ups.'}
                </Text>
              </View>
              <Switch
                accessibilityLabel="Taking sponsors"
                value={sponsorSignupOpen}
                onValueChange={handleToggleSponsorSignupOpen}
                disabled={!branded || busy}
              />
            </View>
          </View>
        )}

        {/* 4. Sponsorships List & Upsert Form */}
        {Boolean(selectedTournamentId) && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>4. Event Sponsors</Text>
            <Text style={styles.hint}>
              Up to 12 approved sponsors per event (at most 1 Presenting). Both Permission on file and Category check passed are required to approve.
            </Text>

            {sponsorships.length === 0 ? (
              <Text style={styles.mutedText}>No sponsorships recorded for this event yet.</Text>
            ) : (
              <View style={styles.sponsorList}>
                {sponsorships.map((sp) => {
                  const canApprove =
                    permissionOnFile === true &&
                    sp.categoryCheckPassed === true &&
                    !busy;
                  return (
                    <View key={sp.sponsorshipId} style={styles.sponsorRow}>
                      <View style={{ flex: 1, gap: 4 }}>
                        <Text style={styles.sponsorRowTitle}>
                          {`${sp.publicName || sp.companyName} [${sp.level}] - ${sp.status}`}
                        </Text>
                        <Text style={styles.sponsorRowMeta}>
                          {`Company: ${sp.companyName || sp.publicName} | Path: ${
                            sp.sponsorPath === 'advertise'
                              ? 'Advertise'
                              : sp.sponsorPath === 'support'
                              ? 'Support'
                              : 'Not specified'
                          } | Sort: ${sp.sortOrder ?? 0} | Category check: ${
                            sp.categoryCheckPassed ? 'Passed' : 'Not passed'
                          }`}
                        </Text>
                        {Boolean(sp.prizeNote) && (
                          <Text style={styles.sponsorRowMeta}>
                            {`Giveaway from ${sp.publicName || sp.companyName}: ${sp.prizeNote}`}
                          </Text>
                        )}
                        {Boolean(sp.logoAssetId) && (
                          <Image
                            source={{ uri: `/sponsor-logo/${sp.logoAssetId}` }}
                            style={styles.logoThumb}
                            resizeMode="contain"
                          />
                        )}
                      </View>
                      <View style={styles.actionGroup}>
                        <TouchableOpacity
                          style={styles.secondaryBtn}
                          onPress={() => startEditSponsorship(sp)}
                          disabled={busy}
                        >
                          <Text style={styles.secondaryBtnText}>Edit</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.secondaryBtn}
                          onPress={() => handlePickAndUploadLogo(sp.sponsorshipId)}
                          disabled={busy}
                        >
                          <Text style={styles.secondaryBtnText}>Upload Logo</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[
                            styles.approveBtn,
                            !canApprove && styles.disabledBtn,
                          ]}
                          onPress={() => handleStatusChange(sp.sponsorshipId, 'approved')}
                          disabled={!canApprove}
                        >
                          <Text style={styles.approveBtnText}>Approve</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.secondaryBtn}
                          onPress={() => handleStatusChange(sp.sponsorshipId, 'declined')}
                          disabled={busy}
                        >
                          <Text style={styles.secondaryBtnText}>Decline</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.dangerBtn}
                          onPress={() => handleStatusChange(sp.sponsorshipId, 'removed')}
                          disabled={busy}
                        >
                          <Text style={styles.dangerBtnText}>Remove</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  );
                })}
              </View>
            )}

            <View style={styles.divider} />
            <Text style={styles.subHeading}>
              {sponsorForm.sponsorshipId ? 'Edit Sponsorship' : 'Add Sponsorship'}
            </Text>

            <Text style={styles.label}>Company name (1-60 chars)</Text>
            <TextInput
              style={styles.input}
              value={sponsorForm.companyName}
              onChangeText={(v) => setSponsorForm((p) => ({ ...p, companyName: v }))}
              placeholder="Legal or company name"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Company name"
              maxLength={60}
            />

            <Text style={styles.label}>Public display name (1-60 chars)</Text>
            <TextInput
              style={styles.input}
              value={sponsorForm.publicName}
              onChangeText={(v) => setSponsorForm((p) => ({ ...p, publicName: v }))}
              placeholder="Public sponsor name"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Public display name"
              maxLength={60}
            />

            <Text style={styles.label}>Level</Text>
            <View style={styles.levelRow}>
              {SPONSOR_LEVELS.map((lv) => (
                <TouchableOpacity
                  key={lv.key}
                  style={[
                    styles.levelChip,
                    sponsorForm.level === lv.key && styles.levelChipActive,
                  ]}
                  onPress={() => setSponsorForm((p) => ({ ...p, level: lv.key }))}
                >
                  <Text
                    style={[
                      styles.levelChipText,
                      sponsorForm.level === lv.key && styles.levelChipTextActive,
                    ]}
                  >
                    {lv.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.switchRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Category check passed</Text>
                <Text style={styles.hint}>
                  Confirm the sponsor is not in a category the game's publisher bans. Capcom bans alcohol, tobacco or vaping, gambling, betting or lottery, firearms, drugs, adult content, political advertising, and anything unsuited to the game's age rating.
                </Text>
              </View>
              <Switch
                accessibilityLabel="Category check passed"
                value={sponsorForm.categoryCheckPassed}
                onValueChange={(v) =>
                  setSponsorForm((p) => ({ ...p, categoryCheckPassed: v }))
                }
                disabled={busy}
              />
            </View>

            <Text style={styles.label}>Category note</Text>
            <TextInput
              style={styles.input}
              value={sponsorForm.categoryNote}
              onChangeText={(v) => setSponsorForm((p) => ({ ...p, categoryNote: v }))}
              placeholder="Category verification note"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Category note"
              maxLength={500}
            />

            <Text style={styles.label}>Sponsor URL (optional, https only)</Text>
            <TextInput
              style={styles.input}
              value={sponsorForm.url}
              onChangeText={(v) => setSponsorForm((p) => ({ ...p, url: v }))}
              placeholder="https://sponsor.example.com"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Sponsor URL (https)"
              maxLength={2048}
            />

            <Text style={styles.label}>Giveaway note (optional, up to 140 chars)</Text>
            <TextInput
              style={styles.input}
              value={sponsorForm.prizeNote}
              onChangeText={(v) => setSponsorForm((p) => ({ ...p, prizeNote: v }))}
              placeholder="Hardware or item description for Giveaway partners"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Giveaway note"
              maxLength={140}
            />

            <Text style={styles.label}>Sort order</Text>
            <TextInput
              style={styles.input}
              value={sponsorForm.sortOrder}
              onChangeText={(v) => setSponsorForm((p) => ({ ...p, sortOrder: v }))}
              placeholder="0"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Sort order"
            />

            <Text style={styles.label}>Contact name (private)</Text>
            <TextInput
              style={styles.input}
              value={sponsorForm.contactName}
              onChangeText={(v) => setSponsorForm((p) => ({ ...p, contactName: v }))}
              placeholder="Contact name"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Contact name"
              maxLength={120}
            />

            <Text style={styles.label}>Contact email (private)</Text>
            <TextInput
              style={styles.input}
              value={sponsorForm.contactEmail}
              onChangeText={(v) => setSponsorForm((p) => ({ ...p, contactEmail: v }))}
              placeholder="contact@example.com"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Contact email"
              maxLength={254}
            />

            <Text style={styles.label}>Contact phone (private)</Text>
            <TextInput
              style={styles.input}
              value={sponsorForm.contactPhone}
              onChangeText={(v) => setSponsorForm((p) => ({ ...p, contactPhone: v }))}
              placeholder="555-0100"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Contact phone"
              maxLength={40}
            />

            <View style={styles.inlineRow}>
              <TouchableOpacity
                style={styles.primaryBtn}
                onPress={handleSaveSponsorship}
                disabled={busy}
              >
                <Text style={styles.primaryBtnText}>
                  {sponsorForm.sponsorshipId ? 'Update Sponsorship' : 'Add Sponsorship'}
                </Text>
              </TouchableOpacity>
              {Boolean(sponsorForm.sponsorshipId || sponsorForm.inquiryId) && (
                <TouchableOpacity
                  style={styles.secondaryBtn}
                  onPress={() => setSponsorForm(EMPTY_SPONSOR_FORM)}
                  disabled={busy}
                >
                  <Text style={styles.secondaryBtnText}>Cancel Edit</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        {/* 5. Championship Page Section */}
        {Boolean(selectedTournamentId) && (
          <View style={styles.card}>
            <View style={styles.inlineRow}>
              <Text style={[styles.sectionTitle, { flex: 1 }]}>5. Championship page</Text>
              {Boolean(savedChampionshipSlug || championshipForm.slug.trim()) && (
                <TouchableOpacity
                  style={styles.secondaryBtn}
                  onPress={() =>
                    navigation?.navigate?.('Championship', {
                      slug: savedChampionshipSlug || championshipForm.slug.trim().toLowerCase(),
                    })
                  }
                >
                  <Text style={styles.secondaryBtnText}>Open page</Text>
                </TouchableOpacity>
              )}
            </View>
            <Text style={styles.hint}>
              Public page at /championship/slug. Slug uses lowercase letters, digits and hyphens (3-60 chars).
            </Text>

            <Text style={styles.label}>Slug (3-60 chars)</Text>
            <TextInput
              style={styles.input}
              value={championshipForm.slug}
              onChangeText={(v) =>
                setChampionshipForm((p) => ({ ...p, slug: v }))
              }
              placeholder="isthmian-sf6-2027"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Championship slug"
              maxLength={60}
              autoCapitalize="none"
            />

            <Text style={styles.label}>Venue name (up to 80 chars)</Text>
            <TextInput
              style={styles.input}
              value={championshipForm.venueName}
              onChangeText={(v) =>
                setChampionshipForm((p) => ({ ...p, venueName: v }))
              }
              placeholder="Isthmian Gaming"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Venue name"
              maxLength={80}
            />

            <Text style={styles.label}>Venue city (up to 80 chars)</Text>
            <TextInput
              style={styles.input}
              value={championshipForm.venueCity}
              onChangeText={(v) =>
                setChampionshipForm((p) => ({ ...p, venueCity: v }))
              }
              placeholder="Madison, WI"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Venue city"
              maxLength={80}
            />

            <Text style={styles.subHeading}>Stages (1-4)</Text>
            {championshipForm.stages.map((st, idx) => (
              <View key={`ch_stage_${idx}`} style={styles.sponsorRow}>
                <View style={{ flex: 1, gap: 8 }}>
                  <Text style={styles.label}>{`Stage ${idx + 1} name`}</Text>
                  <TextInput
                    style={styles.input}
                    value={st.name}
                    onChangeText={(v) =>
                      setChampionshipForm((p) => {
                        const next = p.stages.slice();
                        next[idx] = { ...next[idx], name: v };
                        return { ...p, stages: next };
                      })
                    }
                    placeholder="Online rounds"
                    placeholderTextColor="#6e7688"
                    accessibilityLabel={`Stage ${idx + 1} name`}
                    maxLength={80}
                  />
                  <Text style={styles.label}>{`Stage ${idx + 1} when`}</Text>
                  <TextInput
                    style={styles.input}
                    value={st.when}
                    onChangeText={(v) =>
                      setChampionshipForm((p) => {
                        const next = p.stages.slice();
                        next[idx] = { ...next[idx], when: v };
                        return { ...p, stages: next };
                      })
                    }
                    placeholder="Sat Jan 16, 2027"
                    placeholderTextColor="#6e7688"
                    accessibilityLabel={`Stage ${idx + 1} when`}
                    maxLength={80}
                  />
                  <Text style={styles.label}>{`Stage ${idx + 1} where`}</Text>
                  <TextInput
                    style={styles.input}
                    value={st.where}
                    onChangeText={(v) =>
                      setChampionshipForm((p) => {
                        const next = p.stages.slice();
                        next[idx] = { ...next[idx], where: v };
                        return { ...p, stages: next };
                      })
                    }
                    placeholder="Online on CritCast"
                    placeholderTextColor="#6e7688"
                    accessibilityLabel={`Stage ${idx + 1} where`}
                    maxLength={80}
                  />
                </View>
                {championshipForm.stages.length > 1 && (
                  <TouchableOpacity
                    style={styles.dangerBtn}
                    onPress={() =>
                      setChampionshipForm((p) => ({
                        ...p,
                        stages: p.stages.filter((_, i) => i !== idx),
                      }))
                    }
                  >
                    <Text style={styles.dangerBtnText}>Remove stage</Text>
                  </TouchableOpacity>
                )}
              </View>
            ))}

            {championshipForm.stages.length < 4 && (
              <TouchableOpacity
                style={styles.secondaryBtn}
                onPress={() =>
                  setChampionshipForm((p) => ({
                    ...p,
                    stages: [...p.stages, { ...EMPTY_STAGE }],
                  }))
                }
              >
                <Text style={styles.secondaryBtnText}>Add stage</Text>
              </TouchableOpacity>
            )}

            <Text style={styles.label}>Rules text (plain text, up to 2000 chars)</Text>
            <TextInput
              style={[styles.input, { minHeight: 90 }]}
              value={championshipForm.rulesText}
              onChangeText={(v) =>
                setChampionshipForm((p) => ({ ...p, rulesText: v }))
              }
              placeholder="Tournament rules and match format"
              placeholderTextColor="#6e7688"
              accessibilityLabel="Rules text"
              multiline
              maxLength={2000}
            />

            <View style={styles.inlineRow}>
              <TouchableOpacity
                style={styles.primaryBtn}
                onPress={handleSaveChampionshipPage}
                disabled={busy}
              >
                <Text style={styles.primaryBtnText}>Save Championship Page</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.dangerBtn}
                onPress={handleRemoveChampionshipPage}
                disabled={busy}
              >
                <Text style={styles.dangerBtnText}>Remove Championship Page</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* 6. Sign-ups for this event */}
        {Boolean(selectedTournamentId) && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>6. Sign-ups for this event</Text>
            {eventInquiries.length === 0 ? (
              <Text style={styles.mutedText}>No sponsor sign-ups for this event yet.</Text>
            ) : (
              <View style={styles.sponsorList}>
                {eventInquiries.map((inq) => {
                  const id = inq.inquiryId || inq.id;
                  const dateStr = formatInquiryDate(inq.createdAt);
                  return (
                    <View key={id} style={styles.sponsorRow}>
                      <View style={{ flex: 1, gap: 4 }}>
                        <Text style={styles.sponsorRowTitle}>
                          {`${inq.companyName || 'Company'} (${inq.status || 'new'})`}
                        </Text>
                        <Text style={styles.sponsorRowMeta}>
                          {`Contact: ${inq.contactName || ''} | Email: ${inq.email || ''} | Phone: ${inq.phone || 'N/A'}`}
                        </Text>
                        <Text style={styles.sponsorRowMeta}>
                          {`Path: ${
                            inq.sponsorPath === 'advertise'
                              ? 'Advertise'
                              : inq.sponsorPath === 'support'
                              ? 'Support'
                              : 'Not specified'
                          } | Level interest: ${inq.levelInterest || 'Not sure yet'}${
                            dateStr ? ` | Date: ${dateStr}` : ''
                          }`}
                        </Text>
                        {Boolean(inq.message) && (
                          <Text style={styles.sponsorRowMeta}>
                            {`Message: ${inq.message}`}
                          </Text>
                        )}
                      </View>
                      <View style={styles.actionGroup}>
                        <TouchableOpacity
                          style={styles.secondaryBtn}
                          onPress={() => handleCreateSponsorFromInquiry(inq)}
                          disabled={busy}
                        >
                          <Text style={styles.secondaryBtnText}>
                            Create sponsor from sign-up
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.dangerBtn}
                          onPress={() => handleDeclineInquiry(id)}
                          disabled={busy}
                        >
                          <Text style={styles.dangerBtnText}>Decline</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        )}

        {/* 7. Add a sign-up to this event */}
        {Boolean(selectedTournamentId) && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>7. Add a sign-up to this event</Text>
            {generalInquiries.length === 0 ? (
              <Text style={styles.mutedText}>
                No unassigned general inquiries right now.
              </Text>
            ) : (
              <View style={styles.sponsorList}>
                {generalInquiries.map((inq) => {
                  const id = inq.id || inq.inquiryId;
                  return (
                    <View key={id} style={styles.sponsorRow}>
                      <View style={{ flex: 1, gap: 4 }}>
                        <Text style={styles.sponsorRowTitle}>
                          {inq.companyName || 'Company'}
                        </Text>
                        <Text style={styles.sponsorRowMeta}>
                          {`Contact: ${inq.contactName || ''} | Email: ${inq.email || ''}`}
                        </Text>
                      </View>
                      <TouchableOpacity
                        style={styles.secondaryBtn}
                        onPress={() => handleAssignInquiry(id)}
                        disabled={busy}
                      >
                        <Text style={styles.secondaryBtnText}>Assign</Text>
                      </TouchableOpacity>
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        )}

        {/* 8. Round schedule */}
        {Boolean(selectedTournamentId) && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>Round schedule</Text>
            {roundScheduleLoadError ? (
              <Text style={styles.errorBannerText}>{roundScheduleLoadError}</Text>
            ) : (
              <>
                <Text style={styles.hint}>
                  {`Total rounds: ${eventTotalRounds} | Current mode: ${
                    savedRoundScheduleMode === 'scheduled'
                      ? "Set each round's start time"
                      : `Same gap between rounds (${formatGapLabel(savedInterRoundGapMinutes)})`
                  }`}
                </Text>

                <View style={styles.sponsorList}>
                  {savedRoundScheduleMode !== 'scheduled' && (
                    <Text style={styles.label}>Estimated round starts</Text>
                  )}
                  {Array.from({ length: eventTotalRounds }, (_, idx) => idx + 1).map((r) => {
                    const startMs = r === 1
                      ? eventStartAtMs
                      : (savedRoundScheduleMode === 'scheduled' && Number.isFinite(savedRoundStartTimes[r]))
                        ? savedRoundStartTimes[r]
                        : savedDefaultRoundTimes[r];
                    return (
                      <Text key={`saved_round_${r}`} style={styles.sponsorRowMeta}>
                        {`Round ${r} starts: ${formatRoundLocalTime(startMs)}`}
                      </Text>
                    );
                  })}
                </View>

                {eventTotalRounds >= 2 ? (
                  <>
                    <RoundScheduleEditor
                      mode={roundScheduleMode}
                      onModeChange={handleAdminRoundScheduleModeChange}
                      interRoundGapMinutes={interRoundGapMinutes}
                      onGapChange={handleAdminGapMinutesChange}
                      totalRounds={eventTotalRounds}
                      startAtMs={eventStartAtMs}
                      roundStartTimes={effectiveAdminRoundStartTimes}
                      onRoundTimeChange={handleAdminRoundTimeChange}
                      errorText={roundScheduleError}
                      disabled={busy}
                      DatePickerComponent={DatePicker}
                    />
                    {Boolean(roundScheduleStatus) && (
                      <Text style={styles.statusText}>{roundScheduleStatus}</Text>
                    )}
                    <TouchableOpacity
                      style={styles.primaryBtn}
                      onPress={handleSaveRoundSchedule}
                      disabled={busy}
                    >
                      <Text style={styles.primaryBtnText}>Save round times</Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <Text style={styles.mutedText}>
                    Round schedule applies only to multi-round events.
                  </Text>
                )}
              </>
            )}
          </View>
        )}
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: '#0b0d17',
  },
  scroll: {
    flex: 1,
  },
  container: {
    maxWidth: 980,
    width: '100%',
    alignSelf: 'center',
    padding: 20,
    gap: 18,
  },
  centerBox: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 10,
  },
  deniedTitle: {
    color: '#FFFFFF',
    fontSize: 22,
    fontWeight: '800',
  },
  mutedText: {
    color: '#8b92a8',
    fontSize: 13,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  pageTitle: {
    color: '#FCD000',
    fontSize: 24,
    fontWeight: '900',
  },
  pageSubtitle: {
    color: '#9aa0b4',
    fontSize: 13,
    marginTop: 4,
  },
  backBtn: {
    borderWidth: 1,
    borderColor: '#2a2f4a',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  backBtnText: {
    color: '#E7ECFF',
    fontSize: 13,
    fontWeight: '700',
  },
  errorBanner: {
    backgroundColor: 'rgba(255,90,90,0.14)',
    borderWidth: 1,
    borderColor: '#ff5a5a',
    borderRadius: 8,
    padding: 12,
  },
  errorBannerText: {
    color: '#ff9b9b',
    fontSize: 13,
    fontWeight: '600',
  },
  okBanner: {
    backgroundColor: 'rgba(143,226,162,0.14)',
    borderWidth: 1,
    borderColor: '#8fe2a2',
    borderRadius: 8,
    padding: 12,
  },
  okBannerText: {
    color: '#8fe2a2',
    fontSize: 13,
    fontWeight: '600',
  },
  card: {
    backgroundColor: '#121526',
    borderWidth: 1,
    borderColor: '#232842',
    borderRadius: 12,
    padding: 18,
    gap: 12,
  },
  sectionTitle: {
    color: '#FFFFFF',
    fontSize: 17,
    fontWeight: '800',
  },
  subHeading: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '800',
  },
  label: {
    color: '#E7ECFF',
    fontSize: 13,
    fontWeight: '700',
  },
  hint: {
    color: '#8b92a8',
    fontSize: 12,
  },
  input: {
    backgroundColor: '#0b0d17',
    borderWidth: 1,
    borderColor: '#2a2f4a',
    borderRadius: 8,
    color: '#FFFFFF',
    paddingVertical: 10,
    paddingHorizontal: 12,
    fontSize: 13,
  },
  inlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 4,
  },
  pillWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  eventPill: {
    backgroundColor: '#181c30',
    borderWidth: 1,
    borderColor: '#2a2f4a',
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  eventPillActive: {
    borderColor: '#FCD000',
    backgroundColor: 'rgba(252,208,0,0.12)',
  },
  eventPillText: {
    color: '#E7ECFF',
    fontSize: 12,
    fontWeight: '600',
  },
  selectedMeta: {
    color: '#FCD000',
    fontSize: 12,
    fontWeight: '700',
  },
  primaryBtn: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  primaryBtnText: {
    color: '#000000',
    fontSize: 13,
    fontWeight: '800',
  },
  secondaryBtn: {
    backgroundColor: '#1a1e35',
    borderWidth: 1,
    borderColor: '#2a2f4a',
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  secondaryBtnText: {
    color: '#E7ECFF',
    fontSize: 12,
    fontWeight: '700',
  },
  approveBtn: {
    backgroundColor: '#2ECC71',
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  approveBtnText: {
    color: '#000000',
    fontSize: 12,
    fontWeight: '800',
  },
  dangerBtn: {
    backgroundColor: 'rgba(255,90,90,0.18)',
    borderWidth: 1,
    borderColor: '#ff5a5a',
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  dangerBtnText: {
    color: '#ff9b9b',
    fontSize: 12,
    fontWeight: '700',
  },
  disabledBtn: {
    opacity: 0.4,
  },
  sponsorList: {
    gap: 10,
  },
  sponsorRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    backgroundColor: '#161a2e',
    borderWidth: 1,
    borderColor: '#252a48',
    borderRadius: 8,
    padding: 12,
  },
  sponsorRowTitle: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  sponsorRowMeta: {
    color: '#9aa0b4',
    fontSize: 12,
  },
  logoThumb: {
    width: 96,
    height: 36,
    marginTop: 4,
  },
  actionGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  divider: {
    height: 1,
    backgroundColor: '#232842',
    marginVertical: 6,
  },
  levelRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  levelChip: {
    borderWidth: 1,
    borderColor: '#2a2f4a',
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 14,
  },
  levelChipActive: {
    borderColor: '#FCD000',
    backgroundColor: 'rgba(252,208,0,0.12)',
  },
  levelChipText: {
    color: '#c9d1d9',
    fontSize: 12,
    fontWeight: '600',
  },
  levelChipTextActive: {
    color: '#FCD000',
    fontWeight: '800',
  },
});
